import * as Sentry from "@sentry/react"
import { v4 as uuidv4 } from "uuid"

import { ClientInfoBody, getClientInfo } from "./clientInfo"

// TODO: ちゃんとレビュー

// FE で throw されたエラーを fe_event (event_name = fe_error) として送るための仕組み。
//
// - エラーは render ループ等で短時間に多発しうるので、1 件ずつ送らず buffering して
//   StoreEvents (bulk RPC) でまとめて送る
// - 深掘り (stack 全文・replay) は Sentry 側で行い、こちらは BQ で件数・影響範囲を
//   集計する用途 (params は要点のみ・truncate 済み)
// - 送信失敗時にエラーイベントを積み直すと増幅するので、失敗は Sentry に送って捨てる

export type ErrorEventSource =
	| "error_boundary"
	| "window_error"
	| "unhandled_rejection"
	| "resource_error"

const ERROR_EVENT_NAME = "fe_error"
// buffering してこの間隔でまとめて送る
const FLUSH_INTERVAL_MS = 10_000
// これだけ溜まったら interval を待たず即 flush する
const FLUSH_THRESHOLD = 20
// StoreEvents の buf.validate max_items (50) に合わせた 1 リクエストあたりの上限
const MAX_EVENTS_PER_REQUEST = 50
// keepalive fetch は body 64KB 制限があるため、離脱時 flush は件数をさらに絞る
const MAX_EVENTS_PER_KEEPALIVE_REQUEST = 10
// エラーの嵐 (render ループ等) で送り続けないための 1 セッションあたりの上限
const MAX_EVENTS_PER_SESSION = 100
// 同一エラーの連打をまとめる window
const DEDUPE_WINDOW_MS = 60_000
const MAX_MESSAGE_LENGTH = 500
const MAX_STACK_LENGTH = 2_000

// StoreEventRequest (schema/proto/go/fe_event/v1/fe_event.proto) の JSON 形
type StoreEventRequestBody = ClientInfoBody & {
	event_id: string
	is_test: boolean
	user_id: string
	event_name: string
	location: string
	params: Record<string, string>
}

type NormalizedError = {
	name: string
	message: string
	stack: string | undefined
}

const truncate = (value: string, maxLength: number): string =>
	value.length <= maxLength ? value : value.slice(0, maxLength)

export const normalizeError = (error: unknown): NormalizedError => {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: truncate(error.message, MAX_MESSAGE_LENGTH),
			stack:
				error.stack === undefined
					? undefined
					: truncate(error.stack, MAX_STACK_LENGTH),
		}
	}
	return {
		name: typeof error,
		message: truncate(String(error), MAX_MESSAGE_LENGTH),
		stack: undefined,
	}
}

// 1 SPA に 4 アプリが同居しているため、location だけだと分類しづらい。
// App.tsx のルーティングに合わせて pathname からアプリ名を判定する
export const getAppFromPathname = (pathname: string): string => {
	if (pathname.startsWith("/souhl_admin")) return "souhl_admin"
	if (pathname.startsWith("/company_admin")) return "company_admin"
	if (
		/^\/(rereservation|rereservation_v2|payment|coupon|fin)(\/|$)/.test(
			pathname
		)
	) {
		return "public"
	}
	return "main"
}

const ANONYMOUS_SESSION_ID_STORAGE_KEY = "fe_error_event_session_id"

// 未ログイン (public 画面等) でも同一セッションのエラーを紐付けられるように、
// sessionStorage に匿名 ID を持つ。private mode 等で sessionStorage が throw
// しうるので、その場合は in-memory の ID にフォールバックする
const getAnonymousSessionID = (): string => {
	try {
		const stored = window.sessionStorage.getItem(
			ANONYMOUS_SESSION_ID_STORAGE_KEY
		)
		if (stored) return stored
		const generated = `anonymous-${uuidv4()}`
		window.sessionStorage.setItem(ANONYMOUS_SESSION_ID_STORAGE_KEY, generated)
		return generated
	} catch {
		return `anonymous-${uuidv4()}`
	}
}

type ErrorEventReporterOptions = {
	endpoint: string
	isTest: boolean
}

export class ErrorEventReporter {
	private queue: StoreEventRequestBody[] = []
	private flushTimerID: number | undefined = undefined
	private queuedCount = 0
	private capNoticeQueued = false
	private lastQueuedTimeByDedupeKey = new Map<string, number>()
	private userID: string | undefined = undefined
	private readonly anonymousSessionID: string

	constructor(private readonly options: ErrorEventReporterOptions) {
		this.anonymousSessionID = getAnonymousSessionID()
	}

	// ログイン済みユーザーが分かったタイミングで呼ぶ (useSalon 参照)。
	// 呼ばれるまでは匿名セッション ID が user_id になる
	setUserID(userID: string) {
		this.userID = userID
	}

	report(
		source: ErrorEventSource,
		error: unknown,
		extraParams: Record<string, string>
	) {
		const normalized = normalizeError(error)

		// fe event 送信自体の失敗を拾うと自己増幅するので除外する (loop guard)
		if (
			normalized.message.includes("FeEventService") ||
			(normalized.stack?.includes("FeEventService") ?? false)
		) {
			return
		}

		const dedupeKey = `${source}:${normalized.name}:${normalized.message}`
		const now = Date.now()
		const lastQueuedTime = this.lastQueuedTimeByDedupeKey.get(dedupeKey)
		if (
			lastQueuedTime !== undefined &&
			now - lastQueuedTime < DEDUPE_WINDOW_MS
		) {
			return
		}
		this.lastQueuedTimeByDedupeKey.set(dedupeKey, now)

		if (this.queuedCount >= MAX_EVENTS_PER_SESSION) {
			// 上限到達。「嵐があった」こと自体は BQ から分かるように、上限到達イベントだけ 1 回送る
			if (this.capNoticeQueued) return
			this.capNoticeQueued = true
			this.enqueue(
				this.buildBody(
					source,
					{
						name: "ErrorEventSessionCapReached",
						message: `stopped reporting after ${MAX_EVENTS_PER_SESSION} error events in this session`,
						stack: undefined,
					},
					{}
				)
			)
			return
		}
		this.queuedCount++

		this.enqueue(this.buildBody(source, normalized, extraParams))
	}

	// 離脱・リロード直前の flush。keepalive でページが消えても送信を生き残らせる
	flushForUnload() {
		this.flush(true)
	}

	private buildBody(
		source: ErrorEventSource,
		normalized: NormalizedError,
		extraParams: Record<string, string>
	): StoreEventRequestBody {
		const params: Record<string, string> = {
			app: getAppFromPathname(window.location.pathname),
			source,
			error_name: normalized.name,
			error_message: normalized.message,
			...extraParams,
		}
		if (normalized.stack !== undefined) {
			params.stack = normalized.stack
		}

		return {
			event_id: uuidv4(),
			is_test: this.options.isTest,
			user_id: this.userID ?? this.anonymousSessionID,
			event_name: ERROR_EVENT_NAME,
			location: window.location.href,
			params,
			...getClientInfo(),
		}
	}

	private enqueue(body: StoreEventRequestBody) {
		this.queue.push(body)
		if (this.queue.length >= FLUSH_THRESHOLD) {
			this.flush(false)
			return
		}
		this.scheduleFlush()
	}

	private scheduleFlush() {
		if (this.flushTimerID !== undefined) return
		this.flushTimerID = window.setTimeout(() => {
			this.flushTimerID = undefined
			this.flush(false)
		}, FLUSH_INTERVAL_MS)
	}

	private flush(useKeepalive: boolean) {
		if (this.flushTimerID !== undefined) {
			window.clearTimeout(this.flushTimerID)
			this.flushTimerID = undefined
		}

		const maxCountPerRequest = useKeepalive
			? MAX_EVENTS_PER_KEEPALIVE_REQUEST
			: MAX_EVENTS_PER_REQUEST
		while (this.queue.length > 0) {
			const events = this.queue.splice(0, maxCountPerRequest)
			fetch(
				`${this.options.endpoint}/go.fe_event.v1.FeEventService/StoreEvents`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ events }),
					keepalive: useKeepalive,
				}
			)
				.then((response) => {
					if (!response.ok) {
						throw new Error(
							`Failed to send error events: ${response.statusText}`
						)
					}
				})
				// 送信失敗でエラーイベントを積み直すと増幅するので、Sentry に送って捨てる
				.catch((error) => Sentry.captureException(error))
		}
	}
}

export const errorEventReporter = new ErrorEventReporter({
	endpoint: process.env.REACT_APP_FE_EVENT_POST_STORE_EVENT_API_ADDR ?? "",
	isTest: process.env.REACT_APP_IS_PROD !== "true",
})

// fire-and-forget。呼び出し側で await しない
export const sendErrorEvent = (
	source: ErrorEventSource,
	error: unknown,
	extraParams: Record<string, string>
) => {
	errorEventReporter.report(source, error, extraParams)
}

export const setErrorEventUserID = (userID: string) => {
	errorEventReporter.setUserID(userID)
}

// index.tsx の起動時 (render より前) に 1 回だけ呼ぶ。
// - error (capture): イベントハンドラ・setTimeout 等、ErrorBoundary が拾えない同期 throw。
//   capture: true にすることで img / script 等のリソース読み込み失敗 (bubble しない) も拾う
// - unhandledrejection: .catch の無い Promise の失敗
// - pagehide / visibilitychange: エラー直後の離脱・リロードで buffering 中のイベントを失わないための flush
export const setupGlobalErrorHandlers = () => {
	window.addEventListener(
		"error",
		// リソース読み込み失敗時は ErrorEvent ではなく素の Event が来るため Event で受ける
		(event: Event) => {
			if (event instanceof ErrorEvent) {
				sendErrorEvent("window_error", event.error ?? event.message, {})
				return
			}
			// ErrorEvent でない error イベント = リソース読み込み失敗
			const target = event.target
			if (target instanceof HTMLElement) {
				const { src, href } = target as Partial<
					HTMLImageElement & HTMLLinkElement
				>
				const url = src ?? href ?? ""
				sendErrorEvent(
					"resource_error",
					new Error(`failed to load <${target.tagName.toLowerCase()}>: ${url}`),
					{}
				)
			}
		},
		true
	)

	window.addEventListener("unhandledrejection", (event) => {
		sendErrorEvent("unhandled_rejection", event.reason, {})
	})

	window.addEventListener("pagehide", () => {
		errorEventReporter.flushForUnload()
	})
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") {
			errorEventReporter.flushForUnload()
		}
	})
}
